import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Tag, Plane, BedDouble, Bus, Palmtree, LayoutGrid, Copy, ArrowRight } from "lucide-react";
import { ASSETS, COUPON, DEALS, DEAL_TABS, WHATS_NEW } from "@/mock";
import { toast } from "sonner";

const TAB_ICONS = {
  all: LayoutGrid,
  flight: Plane,
  hotel: BedDouble,
  bus: Bus,
  holiday: Palmtree,
};

// ---------------- Promo Banner + What's New ----------------
export const PromoAndTicker = () => (
  <section className="mx-auto max-w-7xl px-4 pt-8">
    <div className="overflow-hidden rounded-2xl shadow-[0_10px_35px_rgba(27,31,91,0.12)]">
      <img
        src={ASSETS.banner}
        alt="Promotional travel banner"
        className="h-36 w-full object-cover transition-transform duration-700 hover:scale-[1.02] md:h-44"
      />
    </div>

    {/* What's new marquee */}
    <div className="mt-5 flex items-center gap-3 overflow-hidden rounded-full border border-[#F5B32B]/30 bg-[#FFF9EC] py-1.5 pl-1.5 pr-4">
      <span className="shrink-0 rounded-full bg-gradient-to-r from-[#F5B32B] to-[#E89A10] px-4 py-1.5 text-xs font-extrabold uppercase tracking-wider text-white shadow-sm">
        What&apos;s New
      </span>
      <div className="relative flex-1 overflow-hidden">
        <div className="animate-marquee flex w-max gap-12">
          {[...WHATS_NEW, ...WHATS_NEW].map((m, i) => (
            <span key={i} className="whitespace-nowrap text-[13px] font-semibold text-slate-700">
              {m}
            </span>
          ))}
        </div>
      </div>
      <Bell className="h-4.5 w-4.5 shrink-0 animate-pulse text-[#E89A10]" />
    </div>
  </section>
);

// ---------------- Coupon card ----------------
export const CouponSection = () => {
  const navigate = useNavigate();
  const copy = () => {
    navigator.clipboard?.writeText(COUPON.code);
    toast.success(`Coupon ${COUPON.code} copied!`);
  };
  return (
    <section className="mx-auto max-w-7xl px-4 pt-12">
      <h2 className="text-2xl font-extrabold text-slate-800 md:text-[28px]">
        Offers &amp; <span className="text-[#1B1F5B]">Discounts</span>
      </h2>
      <div className="mt-5 grid gap-5 md:grid-cols-[1.2fr_2fr]">
        <div className="relative overflow-hidden rounded-2xl border-2 border-dashed border-[#F5B32B]/60 bg-white p-5 shadow-sm">
          <div className="absolute -left-3 top-1/2 h-6 w-6 -translate-y-1/2 rounded-full bg-[#F5F7FB]" />
          <div className="absolute -right-3 top-1/2 h-6 w-6 -translate-y-1/2 rounded-full bg-[#F5F7FB]" />
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1.5 rounded-md bg-[#1B1F5B]/5 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-[#1B1F5B]">
              <Tag className="h-3.5 w-3.5" /> Coupon
            </span>
            <span className="text-xs text-slate-400">{COUPON.date}</span>
          </div>
          <button
            onClick={copy}
            className="mt-3 flex items-center gap-2 rounded-lg border border-[#F5B32B] bg-[#FFF9EC] px-3 py-1.5 text-sm font-extrabold tracking-widest text-[#B57A00] transition-colors hover:bg-[#FFF3DA]"
          >
            CODE: {COUPON.code} <Copy className="h-3.5 w-3.5" />
          </button>
          <p className="mt-3 text-sm font-semibold text-slate-700">{COUPON.title}</p>
          <button
            data-testid="coupon-view-more"
            onClick={() => navigate("/flight/search")}
            className="mt-3 flex items-center gap-1 text-sm font-bold text-[#1B1F5B] hover:underline"
          >
            View More <ArrowRight className="h-4 w-4" />
          </button>
        </div>
        <div className="overflow-hidden rounded-2xl shadow-sm">
          <img
            src={ASSETS.airlineOffers}
            alt="Airline offers"
            className="h-full min-h-44 w-full object-cover transition-transform duration-700 hover:scale-[1.02]"
          />
        </div>
      </div>
    </section>
  );
};

// ---------------- Deals & Offers ----------------
export const DealsOffers = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("all");
  const list = DEALS.filter((d) => tab === "all" || d.cat === tab);

  const copyCode = (code) => {
    navigator.clipboard?.writeText(code);
    toast.success(`Code ${code} copied!`);
  };

  return (
    <section className="mx-auto max-w-7xl px-4 pt-14">
      <h2 className="text-2xl font-extrabold text-slate-800 md:text-[28px]">
        Deals &amp; <span className="text-[#1B1F5B]">Offers</span>
      </h2>

      {/* Tabs */}
      <div className="no-scrollbar mt-5 flex gap-2.5 overflow-x-auto pb-1">
        {DEAL_TABS.map((t) => {
          const Icon = TAB_ICONS[t.id];
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-all duration-200 ${
                active
                  ? "border-[#1B1F5B] bg-[#1B1F5B] text-white shadow-md"
                  : "border-slate-200 bg-white text-slate-600 hover:border-[#1B1F5B]/40 hover:text-[#1B1F5B]"
              }`}
            >
              <Icon className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Cards */}
      <div className="no-scrollbar -mx-4 mt-5 flex gap-5 overflow-x-auto px-4 pb-4 snap-x">
        {list.map((d) => (
          <div
            key={d.id}
            className="group w-[270px] shrink-0 snap-start overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-[0_6px_24px_rgba(27,31,91,0.08)] transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_16px_40px_rgba(27,31,91,0.16)]"
          >
            <div className="h-36 overflow-hidden">
              <img
                src={d.img}
                alt={d.title}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
            </div>
            <div className="p-4">
              <h3 className="line-clamp-2 min-h-[40px] text-sm font-bold text-slate-800">
                {d.title}
              </h3>
              <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-slate-500">
                {d.desc}
              </p>
              {d.code && (
                <button
                  onClick={() => copyCode(d.code)}
                  className="mt-2.5 flex items-center gap-1.5 rounded-md border border-dashed border-[#F5B32B] bg-[#FFF9EC] px-2 py-1 text-[11px] font-bold text-[#B57A00]"
                >
                  Use Code {d.code} <Copy className="h-3 w-3" />
                </button>
              )}
              <button
                data-testid={`deal-view-more-${d.id}`}
                onClick={() => navigate(d.cat === "hotel" ? "/hotel-page" : d.cat === "bus" ? "/bus" : d.cat === "holiday" ? "/holidays" : "/flight/search")}
                className="mt-3 flex items-center gap-1 text-[13px] font-bold text-[#1B1F5B] transition-transform duration-200 group-hover:translate-x-1"
              >
                View More Details <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
